import { Anchor, Button, Select, TextInput, Textarea } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { api } from '~/trpc-extra/api';
import { useEffect } from 'react';

export const LinearCreateIssueView = ({
  projectId,
  onSuccess,
  prefilledDetails,
  onBack,
}: {
  projectId: string;
  onSuccess?: () => void;
  prefilledDetails?: { title: string; description: string };
  onBack?: () => void;
}) => {
  const { data: availableTeams, isLoading: teamsLoading } = api.linear.getProjectAllowedLinearTeamIds.useQuery({
    projectId,
  });

  const { mutate: createIssue, isPending: createIssuePending } = api.linear.createIssue.useMutation({
    onSuccess: (issueUrl?: string) => {
      notifications.show({
        title: 'Success',
        message: 'Issue created successfully',
        color: 'green',
      });

      if (issueUrl) {
        const w = window.open(issueUrl, '_blank', 'noopener,noreferrer');
        if (w) {
          w.opener = null;
        }
      }

      onSuccess?.();
      modals.closeAll();
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
  });

  const form = useForm({
    initialValues: {
      issueTitle: prefilledDetails?.title ?? '',
      issueDescription: prefilledDetails?.description ?? '',
      teamId: '',
    },
    validate: {
      issueTitle: (value) => (value.length < 1 ? 'Title is required' : null),
      teamId: (value) => (!value ? 'Team selection is required' : null),
    },
  });

  // Set default team to the first available team when teams are loaded
  useEffect(() => {
    if (availableTeams && availableTeams.length > 0 && !form.values.teamId) {
      form.setFieldValue('teamId', availableTeams[0].id);
    }
  }, [availableTeams, form]);

  return (
    <div>
      <form
        className="flex flex-col gap-4"
        onSubmit={form.onSubmit((values) => {
          createIssue({
            projectId,
            issueTitle: values.issueTitle,
            issueDescription: values.issueDescription,
            teamId: values.teamId,
          });
        })}
      >
        <TextInput
          label="Title"
          placeholder="Enter issue title"
          required
          {...form.getInputProps('issueTitle')}
        />

        <Textarea
          label="Description"
          placeholder="Enter issue description"
          rows={5}
          {...form.getInputProps('issueDescription')}
        />

        <div>
          <Select
            label="Team"
            placeholder="Select a team"
            required
            disabled={teamsLoading}
            comboboxProps={{ withinPortal: false }}
            data={availableTeams?.map((team) => ({ value: team.id, label: team.name })) ?? []}
            {...form.getInputProps('teamId')}
          />
          <Anchor
            size="sm"
            className="mt-1 cursor-pointer text-gray-500"
            onClick={() => window.open(`/project/${projectId}/settings?tab=integrations`, '_blank')}
          >
            Don't see your team? Add it here
          </Anchor>
        </div>

        <div className="flex items-center justify-between">
          {!!onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-800"
            >
              <IconArrowLeft size={16} />
              Back
            </button>
          )}
          <div className="ml-auto flex gap-3">
            <Button
              variant="subtle"
              onClick={() => modals.closeAll()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createIssuePending}
            >
              Create
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
